import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

export type PromoBannerData = {
    id: string;
    eyebrow: string;
    headline: string;
    body: string;
    cta: string;
    background: string;
    tone: "light" | "dark";
};

type PromoBannerProps = {
    banner: PromoBannerData;
    className?: string;
};

/**
 * Full-bleed promo card used inside the hero carousel on the Home Dashboard.
 * `tone` controls whether the text should be light or dark; both cases use
 * high-contrast colors relative to the background gradient.
 */
export function PromoBanner({ banner, className }: PromoBannerProps) {
    const textLight = banner.tone === "light";
    return (
        <div
            data-testid={`promo-${banner.id}`}
            className={cn(
                "relative flex h-full min-h-[180px] flex-col justify-between overflow-hidden rounded-3xl p-6 shadow-soft transition hover:shadow-elevated sm:min-h-[220px] sm:p-8",
                banner.background,
                className,
            )}
        >
            {/* Decorative shapes */}
            <div aria-hidden className="pointer-events-none absolute inset-0 opacity-30">
                <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/30 blur-2xl" />
                <div className="absolute -bottom-10 -left-10 h-48 w-48 rounded-full bg-white/20 blur-3xl" />
            </div>

            <div className="relative z-10">
                <p
                    className={cn(
                        "text-xs font-semibold uppercase tracking-widest",
                        textLight ? "text-white/80" : "text-ink/70",
                    )}
                >
                    {banner.eyebrow}
                </p>
                <h3
                    className={cn(
                        "mt-2 max-w-md text-2xl font-extrabold leading-tight sm:text-3xl",
                        textLight ? "text-white" : "text-ink",
                    )}
                >
                    {banner.headline}
                </h3>
                <p
                    className={cn(
                        "mt-2 max-w-sm text-sm",
                        textLight ? "text-white/85" : "text-ink/75",
                    )}
                >
                    {banner.body}
                </p>
            </div>

            <div className="relative z-10 mt-4">
                <button
                    type="button"
                    className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition",
                        textLight
                            ? "bg-white text-ink hover:bg-white/90"
                            : "bg-ink text-white hover:bg-ink/90",
                    )}
                >
                    {banner.cta}
                    <ArrowRight className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

export default PromoBanner;
